import { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Loader2, Upload, ArrowLeft, Sparkles, Plus, X, Lock, Unlock, Check,
  RefreshCw, User, Clapperboard, Trash2, Layers, FolderOpen, Pencil, ChevronRight,
} from "lucide-react";
import { pipelinesApi, streamAsset, mistApi } from "@/api/mist";
import VaultPickButton from "@/components/mist/VaultPickButton";
import UniversalSaveModal from "@/components/mist/UniversalSaveModal";
import { RenderProgress } from "@/components/mist/RenderProgress";

const btn = "font-mono text-[10px] font-black uppercase tracking-widest border-2 border-black flex items-center justify-center gap-1.5";
const CHAR_PROMPT_LABEL = "Character Prompt";
const CHAR_PROMPT_HINT = "Include looks, age, clothing, and mood";
const SCENE_PROMPT_LABEL = "Scene Prompt";
const SCENE_PROMPT_HINT = "Include location, lighting, actions, and palette";
const STYLES = [["realistic", "Realistic"], ["anime", "Anime"], ["cartoon", "Cartoon"], ["caricature", "Caricature"]];
// User's 4-orthogonal blueprint labels (shown in headers for ALL projects, old & new).
const SCENE_POV_LABELS = { a: "POV 1: Counter Angle", b: "POV 2: Table Aisle Angle", c: "POV 3: Reverse Door Angle", d: "POV 4: Window Bar Angle" };
const CHAR_POV_LABELS = { a: "Front", b: "90° Left Profile", c: "90° Right Profile", d: "Back 180°" };
const povLabel = (kind, code) => (kind === "scene" ? SCENE_POV_LABELS : CHAR_POV_LABELS)[(code || "").slice(-1)] || "";

// ---------------------------------------------------------------- Slot card
function SlotCard({ pid, subject, slot, busySlot, slotStage, slotPct, srcs, srcErr, loadSrc,
                    onGenerate, onUpload, onVault, onLock, onUnlock, onClear, gated, gateReason }) {
  const [override, setOverride] = useState("");
  const fileRef = useRef(null);
  const code = slot.code;
  const rendering = busySlot === code;
  const locked = slot.status === "locked";
  const src = slot.image_asset_id ? srcs[slot.image_asset_id] : null;
  const err = slot.image_asset_id ? srcErr[slot.image_asset_id] : false;

  useEffect(() => { if (slot.image_asset_id) loadSrc(slot.image_asset_id); }, [slot.image_asset_id, loadSrc]);

  if (gated) {
    return (
      <div data-testid={`rs-slot-${code}`} className="border-2 border-white/10 bg-[#0b0b0b] p-2 opacity-60">
        <div className="flex items-center gap-1.5 mb-1.5">
          <span className="font-mono text-[9px] font-black uppercase tracking-widest text-white/30">{code}</span>
          <Lock size={11} className="ml-auto text-white/25" />
        </div>
        <div className="aspect-square border-2 border-dashed border-white/10 grid place-items-center text-center px-2">
          <span className="font-mono text-[8px] uppercase tracking-widest text-white/30 leading-tight" data-testid={`rs-slot-gate-${code}`}>{gateReason}</span>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`rs-slot-${code}`}
      className={`border-2 border-black bg-[#101010] p-2 ${locked ? "ring-2 ring-[#98FFB6]" : ""}`}>
      <div className="mb-1.5">
        <div className="flex items-center gap-1.5">
          <span className="font-mono text-[9px] font-black uppercase tracking-widest text-gold">{code}</span>
          <span data-testid={`rs-slot-status-${code}`}
            className={`ml-auto font-mono text-[7px] uppercase tracking-widest px-1.5 py-0.5 border ${
              locked ? "border-[#98FFB6] text-[#98FFB6]" :
              slot.status === "staged" ? "border-[#00F5D4] text-[#00F5D4]" :
              rendering || slot.status === "processing" ? "border-gold text-gold" :
              "border-white/20 text-white/40"}`}>
            {rendering ? "rendering" : slot.status}
          </span>
        </div>
        <span className="block mt-1 font-mono text-[8px] uppercase tracking-wide text-white/60 leading-tight" data-testid={`rs-slot-angle-${code}`}>
          {povLabel(subject.kind, code) || slot.angle}
        </span>
      </div>

      <div className="aspect-square border-2 border-black bg-black grid place-items-center overflow-hidden mb-1.5">
        {rendering ? (
          <div className="w-full px-2"><RenderProgress value={slotPct} label={slotStage || "Rendering"} /></div>
        ) : slot.image_asset_id ? (
          src ? <img src={src} alt={code} data-testid={`rs-slot-img-${code}`} className="w-full h-full object-cover" />
            : err ? (
              <button onClick={() => loadSrc(slot.image_asset_id, true)} data-testid={`rs-slot-retry-${code}`}
                className="grid place-items-center gap-1 text-white/50 hover:text-white">
                <RefreshCw size={14} /><span className="font-mono text-[7px] uppercase tracking-widest">Retry</span>
              </button>
            ) : <Loader2 size={16} className="animate-spin text-white/40" />
        ) : (
          <span className="font-mono text-[8px] text-white/25 uppercase tracking-widest">empty</span>
        )}
      </div>

      {!locked && !rendering && (
        <input value={override} onChange={(e) => setOverride(e.target.value)} data-testid={`rs-slot-prompt-${code}`}
          placeholder="Optional extra direction…"
          className="w-full mb-1.5 bg-black/40 border border-white/10 px-2 py-1 font-mono text-[9px] text-white placeholder:text-white/25 focus:border-gold focus:outline-none" />
      )}

      {locked ? (
        <button onClick={() => onUnlock(code)} data-testid={`rs-slot-unlock-${code}`}
          className={`${btn} w-full bg-[#98FFB6] text-black py-1.5`}><Unlock size={11} /> Locked · Unlock</button>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-1 mb-1">
            <button onClick={() => onGenerate(code, override)} disabled={busySlot !== null} data-testid={`rs-slot-generate-${code}`}
              className={`${btn} bg-gold text-black py-1.5 disabled:opacity-50`}>
              {rendering ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} {slot.image_asset_id ? "Regen" : "Generate"}
            </button>
            <button onClick={() => fileRef.current?.click()} disabled={busySlot !== null} data-testid={`rs-slot-device-${code}`}
              className={`${btn} bg-[#1a1a1a] text-white/70 py-1.5 disabled:opacity-50`}><Upload size={11} /> Device</button>
          </div>
          <div className="grid grid-cols-2 gap-1">
            <VaultPickButton bucket="images" label="Vault" title={`Pick a reference for ${code}`} testid={`rs-slot-vault-${code}`}
              disabled={busySlot !== null}
              className={`${btn} bg-black text-[#00F5D4] border-2 border-[#00F5D4] py-1.5 disabled:opacity-50`}
              onItem={(it) => onVault(code, it)} />
            {slot.image_asset_id ? (
              <button onClick={() => onLock(code)} disabled={busySlot !== null} data-testid={`rs-slot-lock-${code}`}
                className={`${btn} bg-white text-black py-1.5 disabled:opacity-50`}><Lock size={11} /> Lock</button>
            ) : (
              <button disabled className={`${btn} bg-[#1a1a1a] text-white/25 py-1.5`}><Lock size={11} /> Lock</button>
            )}
          </div>
          {slot.image_asset_id && (
            <button onClick={() => onClear(code)} disabled={busySlot !== null} data-testid={`rs-slot-clear-${code}`}
              className={`${btn} w-full bg-transparent text-white/40 border border-white/15 py-1 mt-1`}><X size={10} /> Clear</button>
          )}
          <input ref={fileRef} type="file" accept="image/*" hidden data-testid={`rs-slot-input-${code}`}
            onChange={(e) => { const f = e.target.files?.[0]; if (e.target) e.target.value = ""; if (f) onUpload(code, f); }} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Subject block
function SubjectBlock(props) {
  const { subject } = props;
  const isChar = subject.kind === "character";
  const slots = subject.slots;
  // Sequential step-locking: master (a) first; then reveal the rest. Characters
  // unlock strictly one-by-one (a→b→c→d); scenes unlock b/c/d once master locks.
  const gateFor = (idx) => {
    if (idx === 0 || slots[idx].status === "locked") return { gated: false };
    if (slots[0].status !== "locked") return { gated: true, reason: "Lock the Master first" };
    if (isChar) {
      const prev = slots[idx - 1];
      if (prev.status !== "locked") return { gated: true, reason: `Lock ${prev.code} first` };
    }
    return { gated: false };
  };
  return (
    <div className="border-2 border-black bg-[#161616] p-3" data-testid={`rs-subject-${subject.code}`}>
      <div className="flex items-center gap-2 mb-2">
        {isChar ? <User size={14} className="text-gold shrink-0" /> : <Clapperboard size={14} className="text-[#C9B6FF] shrink-0" />}
        <span className="font-mono text-[10px] font-black uppercase tracking-widest text-white truncate min-w-0 flex-1">
          {subject.code} · {subject.label || (isChar ? "Character" : "Scene")}
        </span>
        <span className="shrink-0 font-mono text-[7px] uppercase tracking-widest text-white/35">
          {slots.filter((s) => s.status === "locked").length}/{slots.length} locked
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {slots.map((slot, idx) => {
          const g = gateFor(idx);
          return <SlotCard key={slot.code} subject={subject} slot={slot} gated={g.gated} gateReason={g.reason} {...props} />;
        })}
      </div>
      <p className="mt-2 font-mono text-[8px] text-white/40 leading-relaxed" data-testid={`rs-seq-hint-${subject.code}`}>
        {isChar
          ? <>Lock the <span className="text-gold">Front master</span> first — each turnaround unlocks in sequence and anchors to it.</>
          : <>Lock the <span className="text-[#C9B6FF]">Master</span> POV first — POVs 2–4 then unlock and lock onto its layout.</>}
      </p>
    </div>
  );
}

// ------------------------------------------------ Large focus editor overlay
// ONE big, high-contrast prompt box for a character/scene. Full-screen focus so
// the whole description is comfortable to read on any device. Colour palette
// matches the Standard Sequential Director (white text on black, gold chars /
// lavender scenes) — just far larger and bolder.
function SubjectFocusEditor({ kind, index, value, onChange, onClose }) {
  const isChar = kind === "character";
  const accentText = isChar ? "text-gold" : "text-[#C9B6FF]";
  const topBorder = isChar ? "border-gold" : "border-[#C9B6FF]";
  const focusBorder = isChar ? "focus:border-gold" : "focus:border-[#C9B6FF]";
  const code = `${isChar ? "R" : "S"}${index + 1}`;
  const label = isChar ? CHAR_PROMPT_LABEL : SCENE_PROMPT_LABEL;
  const hint = isChar ? CHAR_PROMPT_HINT : SCENE_PROMPT_HINT;
  return (
    <div className="fixed inset-0 z-[320] bg-black/90 flex items-end justify-center" data-testid="rs-focus-overlay" onClick={onClose}>
      <div className={`w-full max-w-md bg-[#0d0d0d] border-t-4 ${topBorder} rounded-t-3xl p-5 max-h-[92dvh] overflow-y-auto no-scrollbar flex flex-col gap-4`}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          {isChar ? <User size={20} className={accentText} /> : <Clapperboard size={20} className={accentText} />}
          <div className="flex-1">
            <p className={`font-mono text-[12px] font-black uppercase tracking-widest ${accentText}`}>{code} · {isChar ? "Character" : "Scene"}</p>
            <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">Big-type editor</p>
          </div>
          <button onClick={onClose} data-testid="rs-focus-close" className={`w-9 h-9 grid place-items-center border-2 ${topBorder} ${accentText}`}><X size={17} /></button>
        </div>

        <div className="space-y-1.5">
          <label className="font-mono text-[12px] font-black uppercase tracking-widest text-white">{label}</label>
          <p className="font-mono text-[10px] text-white/50">{hint}</p>
          <textarea rows={9} value={value || ""} autoFocus onChange={(e) => onChange(e.target.value)} data-testid="rs-focus-prompt"
            placeholder={isChar
              ? "e.g. female 20 yo slim build long blonde hair blue eyes wearing blue faded jeans and a knitted cream jumper, she is smiling and is a happy girl."
              : "e.g. red brick coffee shop, wooden counter, product shelves, warm morning light, tables and chairs."}
            className={`w-full bg-black/40 border-2 border-white/20 px-4 py-3.5 font-sans text-lg font-semibold text-white placeholder:text-white/35 leading-relaxed resize-none ${focusBorder} focus:outline-none`} />
        </div>

        <button onClick={onClose} data-testid="rs-focus-done"
          className={`${btn} w-full py-3.5 ${isChar ? "bg-gold" : "bg-[#C9B6FF]"} text-black`}>
          <Check size={15} /> Done
        </button>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------- Studio
export default function RSystemStudio({ onExit, headerExtra }) {
  const [step, setStep] = useState("setup");     // setup | casting | timeline
  const [title, setTitle] = useState("");
  const [aspect, setAspect] = useState("9:16");
  const [style, setStyle] = useState("realistic");   // realistic | anime | cartoon | caricature
  const [characters, setCharacters] = useState([{ prompt: "" }]);
  const [scenes, setScenes] = useState([{ prompt: "" }]);
  const [creating, setCreating] = useState(false);
  const [pid, setPid] = useState(null);
  const [project, setProject] = useState(null);
  const [busySlot, setBusySlot] = useState(null);
  const [slotStage, setSlotStage] = useState("");
  const [slotPct, setSlotPct] = useState(null);
  const [srcs, setSrcs] = useState({});
  const [srcErr, setSrcErr] = useState({});
  const [saveOpen, setSaveOpen] = useState(null);   // asset id being saved
  const [projects, setProjects] = useState([]);     // saved projects for Resume
  const [resuming, setResuming] = useState(null);    // project id being opened
  const [focus, setFocus] = useState(null);          // { kind, index } focus editor
  const [confirmDelete, setConfirmDelete] = useState(null);  // project being erased
  const [weight, setWeight] = useState(0.75);        // IP-Adapter conditioning weight
  // Phase 3 — Timeline Render
  const [shots, setShots] = useState([]);            // [{id, scene_code, character_codes, prompt, seconds, status, image_asset_id, video_asset_id}]
  const [filmBusy, setFilmBusy] = useState(false);
  const [filmStage, setFilmStage] = useState("");
  const [filmPct, setFilmPct] = useState(null);
  const [filmSave, setFilmSave] = useState(null);    // film asset id being committed

  const cleanupRef = useRef({});
  useEffect(() => { cleanupRef.current = srcs; }, [srcs]);
  useEffect(() => () => { Object.values(cleanupRef.current).forEach((u) => u && URL.revokeObjectURL(u)); }, []);

  const loadedRef = useRef({});   // assetId -> loading | done | err
  const loadSrc = useCallback((assetId, force = false) => {
    if (!assetId) return;
    const st = loadedRef.current[assetId];
    if (!force && (st === "loading" || st === "done")) return;
    loadedRef.current[assetId] = "loading";
    setSrcErr((e) => { if (!e[assetId]) return e; const n = { ...e }; delete n[assetId]; return n; });
    (async () => {
      for (let i = 0; i < 3; i += 1) {
        try {
          const u = await streamAsset(assetId);
          loadedRef.current[assetId] = "done";
          setSrcs((s) => ({ ...s, [assetId]: u }));
          return;
        } catch { await new Promise((r) => setTimeout(r, 700 * (i + 1))); }
      }
      loadedRef.current[assetId] = "err";
      setSrcErr((e) => ({ ...e, [assetId]: true }));
    })();
  }, []);

  const refresh = useCallback(async () => {
    if (!pid) return null;
    try { const p = await pipelinesApi.rsGet(pid); setProject(p); return p; }
    catch { return null; }
  }, [pid]);

  const reloadProjects = useCallback(async () => {
    try { const d = await pipelinesApi.rsList(); setProjects(d.projects || []); }
    catch { /* non-blocking */ }
  }, []);

  // Load the Resume list whenever the setup landing view is shown.
  useEffect(() => { if (step === "setup") reloadProjects(); }, [step, reloadProjects]);
  // Sync the weight slider to the opened project.
  useEffect(() => { setWeight(typeof project?.ip_weight === "number" ? project.ip_weight : 0.75); }, [project?.id]); // eslint-disable-line

  const commitWeight = async (v) => {
    setWeight(v);
    if (!pid) return;
    try { await pipelinesApi.rsSetWeight(pid, v); } catch { /* non-blocking */ }
  };

  const eraseProject = async (id) => {
    try {
      await pipelinesApi.rsDelete(id);
      setProjects((ps) => ps.filter((p) => p.id !== id));
      toast.success("Project erased");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't erase that project"); }
    finally { setConfirmDelete(null); }
  };

  const resumeProject = async (id) => {
    setResuming(id);
    try {
      const p = await pipelinesApi.rsGet(id);
      setPid(p.id); setProject(p); setStep("casting");
      toast.success("Project opened");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't open that project"); }
    finally { setResuming(null); }
  };

  // ---- Unified project hydration — W1 (setup) reads from the ACTIVE project so
  // returning to it shows the real characters/scenes/attributes, never blanks.
  // Runs ONCE per project id (open/create/switch), never on slot refreshes, so it
  // can't clobber in-session edits.
  const hydratedRef = useRef(null);
  const hydrateFromProject = useCallback((p) => {
    if (!p) return;
    setTitle(p.title && p.title !== "Untitled pre-production" ? p.title : "");
    setAspect(p.aspect || "9:16");
    setStyle(p.style || "realistic");
    const mapSub = (s) => {
      const master = (s.slots || []).find((sl) => (sl.code || "").endsWith("a") && sl.image_asset_id)
        || (s.slots || []).find((sl) => sl.image_asset_id);
      return { prompt: s.label || "", frontAssetId: master?.image_asset_id || null,
               frontFile: null, frontPreview: null, frontDirty: false,
               masterLocked: !!(master && master.status === "locked") };
    };
    const chars = (p.subjects || []).filter((s) => s.kind === "character").map(mapSub);
    const scns = (p.subjects || []).filter((s) => s.kind === "scene").map(mapSub);
    setCharacters(chars.length ? chars : [{ prompt: "" }]);
    setScenes(scns.length ? scns : [{ prompt: "" }]);
    // Hydrate the device/vault front previews immediately.
    chars.forEach((c, i) => { if (c.frontAssetId) streamAsset(c.frontAssetId).then((u) => setCharacters((arr) => arr.map((x, xi) => (xi === i ? { ...x, frontPreview: u } : x)))).catch(() => {}); });
    scns.forEach((s, i) => { if (s.frontAssetId) streamAsset(s.frontAssetId).then((u) => setScenes((arr) => arr.map((x, xi) => (xi === i ? { ...x, frontPreview: u } : x)))).catch(() => {}); });
  }, []);

  useEffect(() => {
    if (project?.id && hydratedRef.current !== project.id) {
      hydratedRef.current = project.id;
      hydrateFromProject(project);
    }
  }, [project?.id, hydrateFromProject]);

  const updateProject = async () => {
    if (!pid) return;
    const chars = characters.map((c) => ({ kind: "character", label: (c.prompt || "").trim() || ((c.frontAssetId || c.frontFile) ? "Uploaded character" : "character"), attributes: {} }));
    const scns = scenes.map((s) => ({ kind: "scene", label: (s.prompt || "").trim() || ((s.frontAssetId || s.frontFile) ? "Uploaded scene" : "scene"), attributes: {} }));
    if (!chars.length && !scns.length) { toast.error("Keep at least one character or scene"); return; }
    setCreating(true);
    try {
      await pipelinesApi.rsUpdate(pid, { title: title.trim() || null, aspect, style, characters: chars, scenes: scns });
      // Attach any freshly-changed front pictures to the masters (skip locked ones).
      const attach = async (list, prefix, setter) => {
        for (let i = 0; i < list.length; i += 1) {
          const it = list[i];
          if (!it.frontDirty) continue;
          if (it.masterLocked) { toast.error(`Unlock ${prefix}${i + 1} to change its image`); continue; }
          const code = `${prefix}${i + 1}a`;
          try {
            if (it.frontFile) await pipelinesApi.rsSlotUpload(pid, code, it.frontFile);
            else if (it.frontAssetId) await pipelinesApi.rsSlotSetAsset(pid, code, it.frontAssetId);
          } catch (e) { toast.error(`${prefix}${i + 1} front: ${e?.response?.data?.detail || "couldn't attach"}`); }
        }
      };
      await attach(characters, "R");
      await attach(scenes, "S");
      hydratedRef.current = null;                 // allow a clean re-hydrate from the saved state
      setProject(await pipelinesApi.rsGet(pid));
      toast.success("Setup saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save setup"); }
    finally { setCreating(false); }
  };

  const newProject = () => {
    setPid(null); setProject(null); hydratedRef.current = "__new__";
    setTitle(""); setAspect("9:16"); setStyle("realistic");
    setCharacters([{ prompt: "" }]); setScenes([{ prompt: "" }]);
    setStep("setup");
    toast.success("Started a new project");
  };

  const setSubjectPrompt = (setter, idx, val) =>
    setter((arr) => arr.map((x, i) => (i === idx ? { ...x, prompt: val } : x)));
  const setCharFront = (idx, patch) =>
    setCharacters((arr) => arr.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  const setSceneFront = (idx, patch) =>
    setScenes((arr) => arr.map((x, i) => (i === idx ? { ...x, ...patch } : x)));

  // Auto-fire the face-protected outfit swap on Screen-2 arrival for any master
  // slot that has a pre-loaded picture + a prompt modifier (the "handshake").
  const runAutoSwaps = useCallback(async (pidArg, swaps) => {
    for (const sw of swaps) {
      setBusySlot(sw.code); setSlotStage("Restyling outfit"); setSlotPct(8);
      try {
        const g = await pipelinesApi.rsSlotGenerate(pidArg, sw.code, sw.prompt);
        await pipelinesApi.poll(g.job_id, { onTick: (t) => { setSlotStage(t.stage || "Restyling outfit"); setSlotPct(typeof t.progress === "number" ? t.progress : null); } });
      } catch { /* best-effort */ }
      finally { setBusySlot(null); setSlotStage(""); setSlotPct(null); }
    }
    try { const p = await pipelinesApi.rsGet(pidArg); setProject(p); } catch { /* ignore */ }
    if (swaps.length) toast.success("Outfit restyled on your uploaded reference");
  }, []);

  const createProject = async () => {
    const charEntries = characters
      .map((c) => ({ prompt: (c.prompt || "").trim(), frontFile: c.frontFile, frontAssetId: c.frontAssetId }))
      .filter((c) => c.prompt || c.frontFile || c.frontAssetId);
    const sceneEntries = scenes
      .map((s) => ({ prompt: (s.prompt || "").trim(), frontFile: s.frontFile, frontAssetId: s.frontAssetId }))
      .filter((s) => s.prompt || s.frontFile || s.frontAssetId);
    if (!charEntries.length && !sceneEntries.length) { toast.error("Add at least one character or scene prompt"); return; }
    setCreating(true);
    try {
      const p = await pipelinesApi.rsCreate({
        title: title.trim() || null, aspect, style,
        // A pre-loaded picture means its prompt is an OUTFIT MODIFIER, not the
        // subject's identity label — so we keep the header neutral and route the
        // string as a separate swap modifier (below), never into the title.
        characters: charEntries.map((c) => ({ kind: "character", label: (c.frontFile || c.frontAssetId) ? "Uploaded character" : (c.prompt || "character"), attributes: {} })),
        scenes: sceneEntries.map((s) => ({ kind: "scene", label: (s.frontFile || s.frontAssetId) ? "Uploaded scene" : (s.prompt || "scene"), attributes: {} })),
      });
      // Attach any device/vault master pictures onto R#a (characters) and S#a (scenes).
      let attached = false;
      const autoSwaps = [];
      for (let i = 0; i < charEntries.length; i += 1) {
        const c = charEntries[i]; const codeA = `R${i + 1}a`;
        try {
          if (c.frontFile) { await pipelinesApi.rsSlotUpload(p.id, codeA, c.frontFile); attached = true; }
          else if (c.frontAssetId) { await pipelinesApi.rsSlotSetAsset(p.id, codeA, c.frontAssetId); attached = true; }
        } catch { /* attach best-effort */ }
        if ((c.frontFile || c.frontAssetId) && c.prompt) autoSwaps.push({ code: codeA, prompt: c.prompt });
      }
      for (let i = 0; i < sceneEntries.length; i += 1) {
        const s = sceneEntries[i]; const codeA = `S${i + 1}a`;
        try {
          if (s.frontFile) { await pipelinesApi.rsSlotUpload(p.id, codeA, s.frontFile); attached = true; }
          else if (s.frontAssetId) { await pipelinesApi.rsSlotSetAsset(p.id, codeA, s.frontAssetId); attached = true; }
        } catch { /* attach best-effort */ }
        if ((s.frontFile || s.frontAssetId) && s.prompt) autoSwaps.push({ code: codeA, prompt: s.prompt });
      }
      const fresh = attached ? await pipelinesApi.rsGet(p.id) : p;
      setPid(p.id); setProject(fresh); setStep("casting");
      toast.success("Pre-production project created");
      if (autoSwaps.length) runAutoSwaps(p.id, autoSwaps);   // fire-and-forget on arrival
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create the project"); }
    finally { setCreating(false); }
  };

  const generateSlot = async (code, prompt) => {
    setBusySlot(code); setSlotStage("Starting…"); setSlotPct(0);
    try {
      const g = await pipelinesApi.rsSlotGenerate(pid, code, prompt);
      const j = await pipelinesApi.poll(g.job_id, { onTick: (t) => { setSlotStage(t.stage || "Rendering…"); setSlotPct(typeof t.progress === "number" ? t.progress : null); } });
      if (j.status !== "completed") throw new Error(j.error || "Render failed");
      await refresh();
      toast.success(`${code} rendered`);
    } catch (e) { await refresh(); toast.error(e?.response?.data?.detail || e.message || "Render failed"); }
    finally { setBusySlot(null); setSlotStage(""); setSlotPct(null); }
  };

  const uploadSlot = async (code, file) => {
    setBusySlot(code);
    try { const p = await pipelinesApi.rsSlotUpload(pid, code, file); setProject(p); toast.success(`${code} set from device`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusySlot(null); }
  };

  const vaultSlot = async (code, item) => {
    setBusySlot(code);
    try { const p = await pipelinesApi.rsSlotSetAsset(pid, code, item.id); setProject(p); toast.success(`${code} set from Vault`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't set that vault image"); }
    finally { setBusySlot(null); }
  };

  const lockSlot = async (code) => { try { setProject(await pipelinesApi.rsSlotLock(pid, code)); toast.success(`${code} locked`); } catch (e) { toast.error(e?.response?.data?.detail || "Lock failed"); } };
  const unlockSlot = async (code) => { try { setProject(await pipelinesApi.rsSlotUnlock(pid, code)); } catch (e) { toast.error(e?.response?.data?.detail || "Unlock failed"); } };
  const clearSlot = async (code) => { try { setProject(await pipelinesApi.rsSlotClear(pid, code)); } catch (e) { toast.error(e?.response?.data?.detail || "Clear failed"); } };

  const lockedSlots = (project?.subjects || []).flatMap((s) =>
    s.slots.filter((sl) => sl.status === "locked").map((sl) => ({ ...sl, subjectCode: s.code, subjectLabel: s.label })));

  // Subjects that have at least one LOCKED reference — the only ones a shot can cite.
  const lockedScenes = (project?.subjects || [])
    .filter((s) => s.kind === "scene" && s.slots.some((sl) => sl.status === "locked"))
    .map((s) => ({ code: s.code, label: s.label || s.code }));
  const lockedChars = (project?.subjects || [])
    .filter((s) => s.kind === "character" && s.slots.some((sl) => sl.status === "locked"))
    .map((s) => ({ code: s.code, label: s.label || s.code }));

  // Preload previews for locked references when the Timeline step opens.
  useEffect(() => {
    if (step !== "timeline") return;
    lockedSlots.forEach((sl) => loadSrc(sl.image_asset_id));
    if (project?.film_asset_id) loadSrc(project.film_asset_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, project]);

  // Hydrate the shot list from the saved timeline on entering the step / project switch.
  useEffect(() => {
    if (step !== "timeline") return;
    setShots((project?.timeline || []).map((sh) => ({
      id: sh.id, scene_code: sh.scene_code || "", character_codes: sh.character_codes || [],
      prompt: sh.prompt || "", seconds: sh.seconds || 4, status: sh.status || "empty",
      image_asset_id: sh.image_asset_id || null, video_asset_id: sh.video_asset_id || null,
    })));
    (project?.timeline || []).forEach((sh) => sh.image_asset_id && loadSrc(sh.image_asset_id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, project?.id]);

  const addShot = () => {
    if (shots.length >= 12) { toast.error("A timeline holds up to 12 shots"); return; }
    setShots((a) => [...a, {
      id: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      scene_code: lockedScenes[0]?.code || "", character_codes: [], prompt: "", seconds: 4, status: "empty",
    }]);
  };
  const updateShot = (id, patch) => setShots((a) => a.map((s) => (s.id === id ? { ...s, ...patch, status: "empty", image_asset_id: null } : s)));
  const removeShot = (id) => setShots((a) => a.filter((s) => s.id !== id));
  const moveShot = (id, dir) => setShots((a) => {
    const i = a.findIndex((s) => s.id === id); const j = i + dir;
    if (i < 0 || j < 0 || j >= a.length) return a;
    const n = [...a]; [n[i], n[j]] = [n[j], n[i]]; return n;
  });
  const toggleShotChar = (id, code) => setShots((a) => a.map((s) => {
    if (s.id !== id) return s;
    const has = s.character_codes.includes(code);
    return { ...s, character_codes: has ? s.character_codes.filter((c) => c !== code) : [...s.character_codes, code], status: "empty", image_asset_id: null };
  }));

  const renderFilm = async () => {
    const payload = shots
      .map((s) => ({ id: s.id, scene_code: s.scene_code, character_codes: s.character_codes, prompt: (s.prompt || "").trim(), seconds: s.seconds }))
      .filter((s) => s.scene_code || s.character_codes.length);
    if (!payload.length) { toast.error("Add at least one shot with a scene or character"); return; }
    setFilmBusy(true); setFilmStage("Saving timeline"); setFilmPct(2);
    try {
      await pipelinesApi.rsSaveTimeline(pid, payload);
      const r = await pipelinesApi.rsRenderFilm(pid);
      await pipelinesApi.poll(r.job_id, { timeout: 900000, onTick: (t) => { setFilmStage(t.stage || "Rendering film"); setFilmPct(typeof t.progress === "number" ? t.progress : null); } });
      const fresh = await refresh();
      if (fresh?.film_asset_id && fresh?.film_status === "rendered") { loadSrc(fresh.film_asset_id, true); toast.success("Film rendered!"); }
      else toast.error("Render finished but no film was produced — lock the referenced scenes/characters first");
    } catch (e) { toast.error(e?.response?.data?.detail || "Render failed"); }
    finally { setFilmBusy(false); setFilmStage(""); setFilmPct(null); }
  };

  const slotProps = { pid, busySlot, slotStage, slotPct, srcs, srcErr, loadSrc,
    onGenerate: generateSlot, onUpload: uploadSlot, onVault: vaultSlot,
    onLock: lockSlot, onUnlock: unlockSlot, onClear: clearSlot };

  return (
    <div className="h-full w-full flex flex-col overflow-hidden" data-testid="rs-mode-root">
      <div className="flex items-center gap-2 px-4 pt-2 pb-1">
        <button onClick={onExit} data-testid="rs-back" className="text-white/60 hover:text-white"><ArrowLeft size={16} /></button>
        <Layers size={15} className="text-[#C9B6FF]" />
        <span className="font-serif text-lg text-white leading-none">R-System</span>
        <div className="ml-auto">{headerExtra}</div>
      </div>

      {/* Step rail */}
      <div className="flex items-center gap-1 px-4 pb-2" data-testid="rs-steprail">
        {[["setup", "1 · Attributes"], ["casting", "2 · Casting"], ["timeline", "3 · Timeline"]].map(([k, label]) => (
          <button key={k} onClick={() => { if (k !== "setup" && !pid) return; setStep(k); }}
            disabled={k !== "setup" && !pid} data-testid={`rs-step-${k}`}
            className={`${btn} flex-1 py-1.5 ${step === k ? "bg-[#C9B6FF] text-black" : "bg-[#1a1a1a] text-white/45"} disabled:opacity-40`}>
            {label}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-6 space-y-4">
        {/* STEP 1 — SETUP */}
        {step === "setup" && (
          <>
            {/* Resume a saved project — full-card, impossible-to-miss click target */}
            {projects.length > 0 && (
              <div className="space-y-2" data-testid="rs-resume-section">
                <p className="font-mono text-[10px] font-black uppercase tracking-widest text-[#98FFB6]">Resume a project</p>
                {projects.map((p) => (
                  <div key={p.id} className="relative" data-testid={`rs-resume-card-${p.id}`}>
                    <button onClick={() => resumeProject(p.id)} disabled={resuming !== null}
                      data-testid={`rs-resume-${p.id}`}
                      className="w-full text-left border-2 border-[#98FFB6] bg-[#98FFB6]/10 hover:bg-[#98FFB6]/20 p-4 pr-10 flex items-center gap-3 active:translate-y-[1px] disabled:opacity-50 shadow-[3px_3px_0px_#000]">
                      <FolderOpen size={24} className="text-[#98FFB6] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-sans text-base font-bold text-white truncate">{p.title}</p>
                        <p className="font-mono text-[10px] text-white/65 mt-0.5">
                          {p.characters} char · {p.scenes} scene · {p.slots_locked}/{p.slots_total} locked · {p.aspect}
                        </p>
                      </div>
                      {resuming === p.id
                        ? <Loader2 size={20} className="animate-spin text-[#98FFB6] shrink-0" />
                        : <span className="shrink-0 flex items-center gap-0.5 font-mono text-[9px] font-black uppercase tracking-widest bg-[#98FFB6] text-black px-2.5 py-2">Resume<ChevronRight size={12} /></span>}
                    </button>
                    <button onClick={() => setConfirmDelete(p)} disabled={resuming !== null}
                      data-testid={`rs-erase-${p.id}`} title="Erase project"
                      className="absolute top-1.5 right-1.5 w-7 h-7 grid place-items-center border-2 border-white/20 bg-black/70 text-white/50 hover:text-red-400 hover:border-red-400 disabled:opacity-40">
                      <X size={13} />
                    </button>
                  </div>
                ))}
                <div className="flex items-center gap-2 pt-1">
                  <div className="flex-1 h-px bg-white/10" />
                  <span className="font-mono text-[8px] uppercase tracking-widest text-white/35">or start a new project</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>
              </div>
            )}

            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="rs-title"
              placeholder="Project title (optional)"
              className="w-full bg-black/40 border-2 border-white/15 px-3 py-2.5 font-mono text-[12px] text-white placeholder:text-white/40 focus:border-[#C9B6FF] focus:outline-none" />
            <div className="flex items-center gap-1.5">
              {["9:16", "1:1", "16:9"].map((a) => (
                <button key={a} onClick={() => setAspect(a)} data-testid={`rs-aspect-${a.replace(":", "-")}`}
                  className={`${btn} flex-1 py-1.5 ${aspect === a ? "bg-[#C9B6FF] text-black" : "bg-[#1a1a1a] text-white/60"}`}>{a}</button>
              ))}
            </div>

            {/* Global image style — every reference in this project shares one look */}
            <div className="border-2 border-black bg-[#141414] p-3 space-y-2" data-testid="rs-style-section">
              <p className="font-mono text-[11px] font-black uppercase tracking-widest text-white">Image style</p>
              <p className="font-mono text-[10px] text-white/50">Applied to every generated reference so all images match.</p>
              <div className="grid grid-cols-2 gap-1.5">
                {STYLES.map(([k, lb]) => (
                  <button key={k} onClick={() => setStyle(k)} data-testid={`rs-style-${k}`}
                    className={`${btn} py-2.5 ${style === k ? "bg-white text-black" : "bg-[#1a1a1a] text-white/55"}`}>{lb}</button>
                ))}
              </div>
            </div>

            {/* Characters — ONE big prompt box each */}
            <div className="space-y-2">
              <p className="font-mono text-[9px] font-black uppercase tracking-widest text-gold flex items-center gap-1.5"><User size={12} /> Characters (R1–R4) · 4 angles each</p>
              {characters.map((c, i) => (
                <div key={i} className="border-2 border-black bg-[#141414] p-3 space-y-2" data-testid={`rs-char-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[12px] font-black text-gold shrink-0">R{i + 1}</span>
                    <span className="flex-1 font-mono text-[10px] font-black uppercase tracking-widest text-white/70">{CHAR_PROMPT_LABEL}</span>
                    {characters.length > 1 && (
                      <button onClick={() => setCharacters((a) => a.filter((_, x) => x !== i))} data-testid={`rs-char-remove-${i}`} className="text-white/40 hover:text-white shrink-0"><X size={16} /></button>
                    )}
                  </div>
                  <p className="font-mono text-[9px] text-white/45">{CHAR_PROMPT_HINT}</p>
                  <button onClick={() => setFocus({ kind: "character", index: i })} data-testid={`rs-char-focus-${i}`}
                    className={`w-full text-left border-2 px-3 py-3 min-h-[84px] active:translate-y-[1px] ${c.prompt?.trim() ? "border-gold bg-black/40" : "border-white/15 bg-black/30 hover:border-gold"}`}>
                    {c.prompt?.trim()
                      ? <span className="font-sans text-[15px] font-semibold text-white leading-relaxed line-clamp-4">{c.prompt}</span>
                      : <span className="flex items-center gap-2 font-sans text-[15px] text-white/45"><Pencil size={15} className="text-gold shrink-0" /> Tap to write the character prompt</span>}
                  </button>
                  <p className="font-mono text-[8px] text-white/35">Front picture only · neutral background auto-applied</p>
                  {c.frontPreview ? (
                    <div className="flex items-center gap-2 border-2 border-[#00F5D4] bg-[#00F5D4]/10 p-1.5" data-testid={`rs-char-front-set-${i}`}>
                      <img src={c.frontPreview} alt="front" className="w-9 h-9 object-cover border border-black" />
                      <span className="flex-1 font-mono text-[9px] text-[#00F5D4] uppercase tracking-widest">Front image set{c.frontFile ? " · device" : " · vault"}</span>
                      <button onClick={() => setCharFront(i, { frontFile: null, frontAssetId: null, frontPreview: null })} data-testid={`rs-char-front-clear-${i}`} className="text-white/50 hover:text-white"><X size={14} /></button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-1.5">
                      <label htmlFor={`rs-char-front-input-${i}`} data-testid={`rs-char-device-${i}`}
                        className={`${btn} cursor-pointer bg-[#1a1a1a] text-white/70 py-2`}><Upload size={12} /> Device</label>
                      <input id={`rs-char-front-input-${i}`} type="file" accept="image/*" hidden
                        onChange={(e) => { const f = e.target.files?.[0]; if (e.target) e.target.value = ""; if (f) setCharFront(i, { frontFile: f, frontAssetId: null, frontPreview: URL.createObjectURL(f), frontDirty: true }); }} />
                      <VaultPickButton bucket="images" label="Vault" title={`Front picture for R${i + 1}`} testid={`rs-char-vault-${i}`}
                        className={`${btn} bg-black text-[#00F5D4] border-2 border-[#00F5D4] py-2`}
                        onItem={(it) => { setCharFront(i, { frontAssetId: it.id, frontFile: null, frontDirty: true }); streamAsset(it.id).then((u) => setCharFront(i, { frontPreview: u })).catch(() => {}); }} />
                    </div>
                  )}
                </div>
              ))}
              {characters.length < 4 && (
                <button onClick={() => setCharacters((a) => [...a, { prompt: "" }])} data-testid="rs-char-add"
                  className={`${btn} w-full bg-[#1a1a1a] text-white/60 py-2.5`}><Plus size={12} /> Add character</button>
              )}
            </div>

            {/* Scenes — ONE big prompt box each */}
            <div className="space-y-2">
              <p className="font-mono text-[9px] font-black uppercase tracking-widest text-[#C9B6FF] flex items-center gap-1.5"><Clapperboard size={12} /> Scenes (S1–S6) · 4 POVs each</p>
              {scenes.map((s, i) => (
                <div key={i} className="border-2 border-black bg-[#141414] p-3 space-y-2" data-testid={`rs-scene-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[12px] font-black text-[#C9B6FF] shrink-0">S{i + 1}</span>
                    <span className="flex-1 font-mono text-[10px] font-black uppercase tracking-widest text-white/70">{SCENE_PROMPT_LABEL}</span>
                    {scenes.length > 1 && (
                      <button onClick={() => setScenes((a) => a.filter((_, x) => x !== i))} data-testid={`rs-scene-remove-${i}`} className="text-white/40 hover:text-white shrink-0"><X size={16} /></button>
                    )}
                  </div>
                  <p className="font-mono text-[9px] text-white/45">{SCENE_PROMPT_HINT}</p>
                  <p className="font-mono text-[8px] text-white/40 leading-relaxed" data-testid={`rs-scene-povguide-${i}`}>
                    Auto-expands into 4 POVs → <span className="text-[#C9B6FF]">1 Counter Angle</span> · <span className="text-[#C9B6FF]">2 Table Aisle</span> · <span className="text-[#C9B6FF]">3 Reverse Door</span> · <span className="text-[#C9B6FF]">4 Window Bar</span>
                  </p>
                  <button onClick={() => setFocus({ kind: "scene", index: i })} data-testid={`rs-scene-focus-${i}`}
                    className={`w-full text-left border-2 px-3 py-3 min-h-[84px] active:translate-y-[1px] ${s.prompt?.trim() ? "border-[#C9B6FF] bg-black/40" : "border-white/15 bg-black/30 hover:border-[#C9B6FF]"}`}>
                    {s.prompt?.trim()
                      ? <span className="font-sans text-[15px] font-semibold text-white leading-relaxed line-clamp-4">{s.prompt}</span>
                      : <span className="flex items-center gap-2 font-sans text-[15px] text-white/45"><Pencil size={15} className="text-[#C9B6FF] shrink-0" /> Tap to write the scene prompt</span>}
                  </button>
                  <p className="font-mono text-[8px] text-white/35">Master POV picture only · optional reference for S{i + 1}a</p>
                  {s.frontPreview ? (
                    <div className="flex items-center gap-2 border-2 border-[#00F5D4] bg-[#00F5D4]/10 p-1.5" data-testid={`rs-scene-front-set-${i}`}>
                      <img src={s.frontPreview} alt="master" className="w-9 h-9 object-cover border border-black" />
                      <span className="flex-1 font-mono text-[9px] text-[#00F5D4] uppercase tracking-widest">Master image set{s.frontFile ? " · device" : " · vault"}</span>
                      <button onClick={() => setSceneFront(i, { frontFile: null, frontAssetId: null, frontPreview: null })} data-testid={`rs-scene-front-clear-${i}`} className="text-white/50 hover:text-white"><X size={14} /></button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-1.5">
                      <label htmlFor={`rs-scene-front-input-${i}`} data-testid={`rs-scene-device-${i}`}
                        className={`${btn} cursor-pointer bg-[#1a1a1a] text-white/70 py-2`}><Upload size={12} /> Device</label>
                      <input id={`rs-scene-front-input-${i}`} type="file" accept="image/*" hidden
                        onChange={(e) => { const f = e.target.files?.[0]; if (e.target) e.target.value = ""; if (f) setSceneFront(i, { frontFile: f, frontAssetId: null, frontPreview: URL.createObjectURL(f), frontDirty: true }); }} />
                      <VaultPickButton bucket="images" label="Vault" title={`Master POV picture for S${i + 1}`} testid={`rs-scene-vault-${i}`}
                        className={`${btn} bg-black text-[#00F5D4] border-2 border-[#00F5D4] py-2`}
                        onItem={(it) => { setSceneFront(i, { frontAssetId: it.id, frontFile: null, frontDirty: true }); streamAsset(it.id).then((u) => setSceneFront(i, { frontPreview: u })).catch(() => {}); }} />
                    </div>
                  )}
                </div>
              ))}
              {scenes.length < 6 && (
                <button onClick={() => setScenes((a) => [...a, { prompt: "" }])} data-testid="rs-scene-add"
                  className={`${btn} w-full bg-[#1a1a1a] text-white/60 py-2.5`}><Plus size={12} /> Add scene</button>
              )}
            </div>

            {pid ? (
              <div className="space-y-2" data-testid="rs-setup-actions-edit">
                <p className="font-mono text-[9px] uppercase tracking-widest text-[#98FFB6] flex items-center gap-1.5" data-testid="rs-editing-banner">
                  <Check size={11} /> Editing “{project?.title || "this project"}” · changes update this project
                </p>
                <button onClick={updateProject} disabled={creating} data-testid="rs-save-setup"
                  className={`${btn} w-full bg-[#98FFB6] text-black py-3 disabled:opacity-50`}>
                  {creating ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save setup changes
                </button>
                <button onClick={() => setStep("casting")} data-testid="rs-continue-casting"
                  className={`${btn} w-full bg-[#C9B6FF] text-black py-3`}>
                  Continue to Casting <ChevronRight size={13} />
                </button>
                <button onClick={newProject} data-testid="rs-new-project"
                  className={`${btn} w-full bg-[#1a1a1a] text-white/55 border-2 border-white/15 py-2.5`}>
                  <Plus size={12} /> New project
                </button>
              </div>
            ) : (
              <button onClick={createProject} disabled={creating} data-testid="rs-create-btn"
                className={`${btn} w-full bg-[#C9B6FF] text-black py-3 disabled:opacity-50`}>
                {creating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Create Project &amp; Start Casting
              </button>
            )}
          </>
        )}

        {/* STEP 2 — CASTING */}
        {step === "casting" && project && (
          <>
            {/* IP-Adapter conditioning weight — global per project */}
            <div className="border-2 border-black bg-[#141414] p-3" data-testid="rs-weight-panel">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-[10px] font-black uppercase tracking-widest text-white">Reference weight</span>
                <span className="font-mono text-[11px] font-black text-gold" data-testid="rs-weight-value">{weight.toFixed(2)}</span>
              </div>
              <input type="range" min="0" max="1" step="0.05" value={weight} data-testid="rs-weight-slider"
                onChange={(e) => setWeight(parseFloat(e.target.value))}
                onMouseUp={(e) => commitWeight(parseFloat(e.target.value))}
                onTouchEnd={(e) => commitWeight(parseFloat(e.target.value))}
                className="w-full accent-gold" />
              <div className="flex justify-between font-mono text-[8px] uppercase tracking-widest text-white/40 mt-1">
                <span>0 · prompt freedom</span><span>rigid reference · 1</span>
              </div>
              <p className="font-mono text-[8px] text-white/40 mt-1.5 leading-relaxed">Applies when a prompt <span className="text-[#00F5D4]">@mentions</span> a locked character/scene (multi-image IP-Adapter).</p>
            </div>
            <p className="font-mono text-[9px] text-white/45 leading-relaxed">
              Generate, upload or pick each reference angle, then <span className="text-[#98FFB6]">Lock</span> it. Locked references
              are your continuity vault — reused across the timeline in Phase 3.
            </p>
            {project.subjects.map((s) => <SubjectBlock key={s.code} subject={s} {...slotProps} />)}
            <button onClick={() => setStep("timeline")} data-testid="rs-goto-timeline"
              className={`${btn} w-full bg-[#C9B6FF] text-black py-3`}><ArrowLeft size={13} className="rotate-180" /> Go to Timeline</button>
          </>
        )}

        {/* STEP 3 — TIMELINE RENDER (Phase 3) */}
        {step === "timeline" && project && (
          <>
            <div className="border-2 border-[#C9B6FF] bg-[#C9B6FF]/10 p-3">
              <p className="font-mono text-[10px] font-black uppercase tracking-widest text-[#C9B6FF] mb-1">Timeline · sequence your film</p>
              <p className="font-mono text-[9px] text-white/50 leading-relaxed">Arrange shots from your locked scenes &amp; characters, then render one stitched film (720p · Option-C).</p>
            </div>

            {(lockedScenes.length === 0 && lockedChars.length === 0) ? (
              <p className="font-mono text-[10px] text-white/40 py-8 text-center" data-testid="rs-timeline-empty">
                No locked references yet — lock at least one scene or character in the Casting step first.
              </p>
            ) : (
              <>
                {/* Shot builder */}
                <div className="space-y-2.5" data-testid="rs-shot-list">
                  {shots.map((sh, i) => (
                    <div key={sh.id} className="border-2 border-black bg-[#101010] p-2.5 space-y-2" data-testid={`rs-shot-${i}`}>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] font-black uppercase tracking-widest text-gold">Shot {i + 1}</span>
                        {sh.status === "done" && <Check size={12} className="text-[#98FFB6]" />}
                        <div className="ml-auto flex items-center gap-1">
                          <button onClick={() => moveShot(sh.id, -1)} disabled={i === 0} data-testid={`rs-shot-up-${i}`} className="w-6 h-6 grid place-items-center border border-white/20 text-white/60 disabled:opacity-30"><ChevronRight size={12} className="-rotate-90" /></button>
                          <button onClick={() => moveShot(sh.id, 1)} disabled={i === shots.length - 1} data-testid={`rs-shot-down-${i}`} className="w-6 h-6 grid place-items-center border border-white/20 text-white/60 disabled:opacity-30"><ChevronRight size={12} className="rotate-90" /></button>
                          <button onClick={() => removeShot(sh.id)} data-testid={`rs-shot-del-${i}`} className="w-6 h-6 grid place-items-center border border-red-500/40 text-red-400"><Trash2 size={12} /></button>
                        </div>
                      </div>

                      {sh.image_asset_id && (
                        <div className="aspect-video border-2 border-black bg-black overflow-hidden grid place-items-center">
                          {srcs[sh.image_asset_id]
                            ? <img src={srcs[sh.image_asset_id]} alt={`shot ${i + 1}`} className="w-full h-full object-cover" />
                            : <Loader2 size={16} className="animate-spin text-white/40" />}
                        </div>
                      )}

                      {/* Scene backdrop */}
                      <div>
                        <label className="font-mono text-[8px] uppercase tracking-widest text-white/40 flex items-center gap-1 mb-1"><Clapperboard size={10} className="text-[#C9B6FF]" /> Scene backdrop</label>
                        <select value={sh.scene_code} onChange={(e) => updateShot(sh.id, { scene_code: e.target.value })} data-testid={`rs-shot-scene-${i}`}
                          className="w-full bg-black border-2 border-white/15 focus:border-[#C9B6FF]/60 outline-none px-2 py-2 text-[13px] text-white font-sans">
                          <option value="">— none —</option>
                          {lockedScenes.map((s) => <option key={s.code} value={s.code}>{s.code} · {s.label}</option>)}
                        </select>
                      </div>

                      {/* Characters in this shot */}
                      {lockedChars.length > 0 && (
                        <div>
                          <label className="font-mono text-[8px] uppercase tracking-widest text-white/40 flex items-center gap-1 mb-1"><User size={10} className="text-[#98FFB6]" /> Characters in shot</label>
                          <div className="flex flex-wrap gap-1.5" data-testid={`rs-shot-chars-${i}`}>
                            {lockedChars.map((c) => {
                              const on = sh.character_codes.includes(c.code);
                              return (
                                <button key={c.code} onClick={() => toggleShotChar(sh.id, c.code)} data-testid={`rs-shot-char-${i}-${c.code}`}
                                  className={`font-mono text-[9px] uppercase tracking-widest border-2 px-2 py-1 ${on ? "border-[#98FFB6] bg-[#98FFB6]/15 text-[#98FFB6]" : "border-white/15 text-white/50"}`}>
                                  {c.code} · {c.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Action / camera + duration */}
                      <textarea value={sh.prompt} onChange={(e) => updateShot(sh.id, { prompt: e.target.value })} data-testid={`rs-shot-prompt-${i}`}
                        rows={2} placeholder="Action & camera — e.g. the hero walks toward the door, slow push-in"
                        className="w-full bg-black border-2 border-white/15 focus:border-[#C9B6FF]/60 outline-none px-2 py-2 text-[13px] text-white font-sans resize-none" />
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[8px] uppercase tracking-widest text-white/40">Duration</span>
                        <div className="flex gap-1">
                          {[2, 3, 4, 5, 6].map((sec) => (
                            <button key={sec} onClick={() => updateShot(sh.id, { seconds: sec })} data-testid={`rs-shot-sec-${i}-${sec}`}
                              className={`font-mono text-[9px] w-7 h-7 border-2 ${sh.seconds === sec ? "border-gold bg-gold text-black" : "border-white/15 text-white/50"}`}>{sec}s</button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}

                  <button onClick={addShot} disabled={shots.length >= 12} data-testid="rs-add-shot"
                    className={`${btn} w-full bg-[#1a1a1a] text-[#C9B6FF] border-2 border-[#C9B6FF]/40 py-3 disabled:opacity-40`}>
                    <Plus size={13} /> Add shot {shots.length ? `(${shots.length}/12)` : ""}
                  </button>
                </div>

                {/* Render */}
                {filmBusy ? (
                  <div className="border-2 border-black bg-[#0d0d0d] p-4 flex flex-col items-center gap-2" data-testid="rs-film-progress">
                    <RenderProgress value={filmPct} label={filmStage || "Rendering film"} testid="rs-film-render-progress" />
                  </div>
                ) : (
                  <button onClick={renderFilm} disabled={shots.length === 0} data-testid="rs-render-film"
                    className={`${btn} w-full bg-[#C9B6FF] text-black py-3.5 disabled:opacity-40`}>
                    <Sparkles size={14} /> Render film · {shots.length} shot{shots.length === 1 ? "" : "s"}
                  </button>
                )}

                {/* Final film output */}
                {project.film_asset_id && project.film_status === "rendered" && !filmBusy && (
                  <div className="border-2 border-[#98FFB6] bg-[#98FFB6]/10 p-2.5 space-y-2" data-testid="rs-film-output">
                    <p className="font-mono text-[10px] font-black uppercase tracking-widest text-[#98FFB6]">Your film</p>
                    <div className="border-2 border-black bg-black overflow-hidden">
                      {srcs[project.film_asset_id]
                        ? <video src={srcs[project.film_asset_id]} controls playsInline className="w-full" data-testid="rs-film-video" />
                        : <div className="aspect-video grid place-items-center"><Loader2 size={18} className="animate-spin text-white/40" /></div>}
                    </div>
                    <button onClick={() => setFilmSave(project.film_asset_id)} data-testid="rs-film-commit"
                      className={`${btn} w-full bg-white text-black py-2.5`}><Check size={12} /> Commit film to Vault</button>
                  </div>
                )}
              </>
            )}

            {/* Locked references — commit individually to the permanent Vault */}
            {lockedSlots.length > 0 && (
              <details className="border-2 border-white/10 bg-[#0b0b0b]" data-testid="rs-locked-refs">
                <summary className="font-mono text-[9px] font-black uppercase tracking-widest text-white/55 px-3 py-2 cursor-pointer">Locked references · {lockedSlots.length}</summary>
                <div className="grid grid-cols-3 gap-2 p-2">
                  {lockedSlots.map((sl) => (
                    <div key={sl.code} className="border-2 border-black bg-[#101010] p-1.5" data-testid={`rs-timeline-${sl.code}`}>
                      <div className="aspect-square border-2 border-black bg-black overflow-hidden mb-1 grid place-items-center">
                        {srcs[sl.image_asset_id]
                          ? <img src={srcs[sl.image_asset_id]} alt={sl.code} className="w-full h-full object-cover" />
                          : srcErr[sl.image_asset_id]
                            ? <button onClick={() => loadSrc(sl.image_asset_id, true)} className="text-white/50"><RefreshCw size={13} /></button>
                            : <Loader2 size={14} className="animate-spin text-white/40" />}
                      </div>
                      <button onClick={() => setSaveOpen({ id: sl.image_asset_id, category: (sl.subjectCode || "").startsWith("R") ? "character" : "scene" })} data-testid={`rs-timeline-save-${sl.code}`}
                        className={`${btn} w-full bg-white text-black py-1`}><Check size={10} /> Save</button>
                    </div>
                  ))}
                </div>
              </details>
            )}

            <button onClick={() => setStep("casting")} data-testid="rs-back-to-casting"
              className={`${btn} w-full bg-[#1a1a1a] text-white/60 py-2.5`}><RefreshCw size={12} /> Back to Casting</button>
          </>
        )}
      </div>

      {focus && (focus.kind === "character" ? characters[focus.index] : scenes[focus.index]) && (
        <SubjectFocusEditor
          kind={focus.kind}
          index={focus.index}
          value={(focus.kind === "character" ? characters[focus.index] : scenes[focus.index]).prompt}
          onChange={(val) => setSubjectPrompt(focus.kind === "character" ? setCharacters : setScenes, focus.index, val)}
          onClose={() => setFocus(null)}
        />
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-[330] bg-black/90 flex items-center justify-center p-6" data-testid="rs-erase-confirm" onClick={() => setConfirmDelete(null)}>
          <div className="w-full max-w-sm bg-[#0d0d0d] border-2 border-red-500 p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <p className="font-mono text-[12px] font-black uppercase tracking-widest text-red-400">Erase project?</p>
            <p className="font-sans text-sm text-white/70 leading-relaxed">
              “{confirmDelete.title}” will be removed from your Resume list. Committed Vault images are kept.
            </p>
            <div className="grid grid-cols-2 gap-2 pt-1">
              <button onClick={() => setConfirmDelete(null)} data-testid="rs-erase-cancel"
                className={`${btn} bg-[#1a1a1a] text-white/70 py-2.5`}>Cancel</button>
              <button onClick={() => eraseProject(confirmDelete.id)} data-testid="rs-erase-confirm-btn"
                className={`${btn} bg-red-500 text-white py-2.5`}><Trash2 size={13} /> Erase</button>
            </div>
          </div>
        </div>
      )}

      {saveOpen && (
        <UniversalSaveModal open onClose={() => setSaveOpen(null)} assetId={saveOpen.id}
          track="IMAGES" isMusic={false} defaultName="R-System reference"
          communityDefaultCategory={saveOpen.category}
          onSaved={() => { setSaveOpen(null); toast.success("Committed to Vault"); }} />
      )}

      {filmSave && (
        <UniversalSaveModal open onClose={() => setFilmSave(null)} assetId={filmSave}
          track="VIDEOS" isMusic={false} defaultName={`${project?.title || "R-System"} · film`}
          onSaved={() => { setFilmSave(null); toast.success("Film committed to Vault"); }} />
      )}
    </div>
  );
}
